/* Реестр движков экранов студии (engine-kit) и хозяин кадров движка. engineFor() (engine-screens.ts) выбирает только
   зарегистрированный экран: голос регистрирует features/voice/module.ts в серверном режиме, а выбирает его только список экранов
   сборки (engine-default.json — пустой до коммита переключения). Кадр движка — EngineFrame.tsx: он зовёт engineHost.enter/leave,
   пока привязан, а живые side/sideOpen/sideKind раздела (studio/module.ts) читают отсюда.
   Ключ реестра — каноническое имя экрана (canonicalScreen: circle, lipsync → who; swap → dance); экран входа приложения
   находит свой движок по нему или по псевдонимам движка (aliases). */
import type { StudioApp } from '../studio-api';
import type { StudioOrderSnapshot } from '../studio-job-bridge';
import { canonicalScreen } from './engine-list';
import type { OrderService, OrderSnapshot } from './order-service';

/** Вид знака заказа над меню (studio-job-bridge) */
export type EngineKind = StudioOrderSnapshot['kind'];
/** Вид знака по состоянию сервиса заказа и приложению кадра: null — знака нет (инструменты «Текста» без хвоста заказа) */
export type EngineKindResolver = (s: OrderSnapshot, app: StudioApp) => EngineKind | null;

/** Режимы экрана в одном кадре (photo, video: каждый режим — своё приложение сервера со своим сервисом заказа, mode-group.tsx).
    Движок держит свою разметку и S, сервер-объект экрана переводит себя на сервис текущего режима. */
export interface EngineModes {
  /** Текущий режим: приложение и его сервис (до первой смены — приложение кадра) */
  current(): { app: StudioApp; svc: OrderService };
  /** Сервис смонтированного режима (приложение кадра, текущий и прежний до готовности текущего) или null */
  byApp(id: number): OrderService | null;
  /** Смена режима (mode-switch.ts: владелец до и после GET приложения, тот же экран, черновики владелец:приложение, 30 с).
      seed — входы текущего режима: его черновик, а с transfer — и входы нового поверх его черновика. rights — перенести согласие
      на те же файлы (Q-PV1). true — сервис нового режима смонтирован (его форма может ещё грузиться); false — режим не сменился. */
  switchTo(id: number, seed: Record<string, string>, o?: { transfer?: boolean; rights?: boolean }): Promise<boolean>;
  /** Смена режима идёт */
  busy(): boolean;
}

/** То, что кадр студии отдаёт движку при привязке */
export interface EngineBinding {
  /** Ключ кадра (не owner:app): два кадра одного приложения — разные привязки */
  key: string;
  svc: OrderService;
  app: StudioApp;
  initialInputs?: Record<string, unknown>;
  /** Экран открыт кнопкой «Попробовать» листа шаблона (templateEntry кадра ServerStudio) */
  templateEntry?: boolean;
  /** Режимы экрана: только у экрана с EngineScreen.modes */
  modes?: EngineModes;
  /** Любое «назад» движка идёт через ServerStudio.back() */
  back(): void;
  active(): boolean;
}
export interface EngineBound {
  /** Вид сервиса заказа изменился (у экрана с режимами — вид любого из них) */
  changed(): void;
  release(): void;
  /** «Назад» внутри экрана (каталог ← шаблон рилса, лист стилей): кадр спрашивает его первым — и на кнопке «назад» движка,
      и на «назад» раздела (Telegram, Escape). true — движок сам сделал шаг назад, раздел остаётся на экране */
  back?(): boolean;
  /** Готовность первой отрисовки (списки из формы и документа экрана): смена вида ServerStudio ждёт её не дольше READY_MS */
  ready?(): Promise<void>;
}
export interface EngineScreen {
  /** Глобальный объект движка шаблона (window.BotVoice) */
  global: string;
  /** Префикс data-атрибутов движка (data-vc-back) */
  prefix: string;
  /** Экран — боковой (#bot.sd) */
  side: boolean;
  /** Вид знака заказа над меню (studio-job-bridge) — постоянный или по состоянию сервиса; без него (или null) знака нет */
  kind?: EngineKind | EngineKindResolver;
  /** Другие экраны входа, которые открывает этот движок (кроме канонических псевдонимов canonicalScreen) */
  aliases?: readonly string[];
  /** Каждый режим экрана — своё приложение сервера: кадр держит их сервисы (mode-group.tsx) и отдаёт движку EngineBinding.modes */
  modes?: boolean;
  bind(b: EngineBinding): EngineBound;
}
/** Глобальный объект движка шаблона: open(хозяин, вход), close() без перехода (onClose не задан), mounted */
export interface EngineGlobal {
  open(host: HTMLElement, o: Record<string, unknown>): void;
  close(): void;
  readonly mounted?: boolean;
}

/** Зарегистрированные движки по каноническому имени экрана (canonicalScreen(app.entry.screen)) */
export const ENGINES: Record<string, EngineScreen> = {};

/** Движок экрана входа: по каноническому имени, иначе по псевдонимам движков. name — его ключ в ENGINES.
    Только собственные ключи реестра: унаследованные не считаются. */
export function engineEntry(screen: string): { name: string; engine: EngineScreen } | null {
  const name = canonicalScreen(screen);
  if (!name) return null;
  if (Object.hasOwn(ENGINES, name)) return { name, engine: ENGINES[name] };
  for (const key of Object.keys(ENGINES)) {
    const engine = ENGINES[key];
    if (engine.aliases?.some(alias => canonicalScreen(alias) === name)) return { name: key, engine };
  }
  return null;
}

/** Вид знака заказа экрана для снимка сервиса: постоянный kind, ответ разрешителя или null (знака нет) */
export function engineKind(engine: EngineScreen | null | undefined, s: OrderSnapshot, app: StudioApp): EngineKind | null {
  const kind = engine?.kind;
  return (typeof kind === 'function' ? kind(s, app) : kind) ?? null;
}

/** Глобальный объект движка экрана, если он загружен */
export function engineGlobal(screen: string): EngineGlobal | null {
  const engine = engineEntry(screen)?.engine;
  if (!engine) return null;
  const found = (window as unknown as Record<string, unknown>)[engine.global];
  return found && typeof found === 'object' ? (found as EngineGlobal) : null;
}

/** Экраны привязанных кадров движка в порядке входа (кадр входит, когда становится активным, и выходит, когда перестаёт) */
const entered: string[] = [];
const sideScreen = (screen: string) => !!engineEntry(screen)?.engine.side;

export const engineHost = {
  enter(screen: string) {
    entered.push(screen);
  },
  /** Выходит один вход этого экрана: StrictMode и смена кадров одного экрана не снимают чужой вход */
  leave(screen: string) {
    const at = entered.lastIndexOf(screen);
    if (at >= 0) entered.splice(at, 1);
  },
  /** Нужна правая панель (.sd): активен кадр бокового экрана — на форме и на хвосте заказа, как side шаблона (studio/engine.ts) */
  side(): boolean {
    return entered.some(sideScreen);
  },
  /** Боковой экран сейчас открыт: его движок смонтирован (sideOpen шаблона) */
  sideOpen(): boolean {
    return entered.some(screen => sideScreen(screen) && !!engineGlobal(screen)?.mounted);
  },
  /** Вид открытого экрана движка — экран входа кадра ('voice', 'circle'…), как sideKind шаблона, или '' */
  sideKind(): string {
    return entered.at(-1) ?? '';
  },
};
